import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { styled } from "@mui/material/styles";

import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";

import ButtonBlue from "../../../../shared/general/ButtonBlue";

import utils from "../../../../../../utils";

import { deleteEmployeeSalaryChange, getEmployeeProfile, getEmployeeSalaryChange } from "../../../../../../actions/employee";

import DrawerEditSalaryChange from "./DrawerEditSalaryChange";
import Swal from "sweetalert2";

const StyledRoot = styled("div")({
  "& .salary": {
    "& .MuiTypography-h6": {
      marginRight: 8,
    },
    display: "flex",
    alignItems: "baseline",
  },
});

const StyledHeadLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
});

const StyledWrapTop = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  marginBottom: 16,
  "& .MuiTypography-body2": {
    margin: 0,
  },
});

const StyledDivider = styled(Divider)({
  marginTop: 16,
  marginBottom: 16,
  borderWidth: "0px 0px thin",
  borderColor: "#919eab52",
  borderStyle: "dashed",
  width: "100%",
});

const StyledTextValue = styled(Typography)({
  fontSize: 18,
  "& i": {
    padding: 2,
  },
});

const StyledWrapChange = styled("div")({
  marginTop: 8,
  "& .inner": {
    marginBottom: 8,
  },
  "& .first-line": {
    display: "flex",
    "& .date": {
      margin: "0 8px",
      fontWeight: 600,
    },
    "& .positions-name": {
      fontWeight: 600,
    },
  },
  "& .second-line": {
    marginLeft: 16,
  },
});

function PaymentChange(props) {
  const dispatch = useDispatch();
  const { result: employeeProfile } = useSelector(
    (state) => state.employeeProfile
  );
  const { result: employeeSalary } = useSelector(
    (state) => state.employeeSalary
  );
  const { t, i18n } = useTranslation();

  const [showPersonalId, setShowPersonalId] = useState(false);

  const [drawerEditSalaryChangeConfig, setDrawerEditSalaryChangeConfig] = useState({
    isOpen: false,
    data: {}
  })

  useEffect(() => {
    dispatch(getEmployeeSalaryChange(employeeProfile.idEmp));
  }, []);

  const handleOpenDrawerAdd = () => {
    setDrawerEditSalaryChangeConfig({
      isOpen: true,
      isEdit: false,
      data: {},
    })
  }

  const handleClickEditSalaryChange = (data) => {
    setDrawerEditSalaryChangeConfig(prev => ({
      ...prev,
      isOpen: true,
      isEdit: true,
      data: {
        idEmployeeSalary: data.idEmployeeSalary,
        idPaymentType: data.idPaymentType,
        salaryValue: data.salaryValue,
        idEmp: data.idEmp,
        start: data.start,
        end: data.end,
        remark: data.remark,
      }
    }))
  }

  const handleCloseEditSalaryChange = (data) => {
    setDrawerEditSalaryChangeConfig(prev => ({
      ...prev,
      isOpen: false,
    }))
  }

  const renderDate = (value) => {
    if(!value) return t("Current");

    if(i18n.language === "th"){
      return dayjs(value).locale("th").format("DD MMM BBBB");
    } else {
      return dayjs(value).locale("en").format("DD MMM YYYY");
    }
  }

  const handleClickDeleteEmployeeSalaryChange = (data) => {
    Swal.fire({
      title: t("AreYouSure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#F15E5E",
      confirmButtonText: t("Delete"),
      cancelButtonText: t("Cancel"),
      customClass: {
        container: "my-swal-container",
      }
    }).then(async (result) => {

      if (result.isConfirmed) {

        const deleteResult = await dispatch(deleteEmployeeSalaryChange(data.idEmp, data.idEmployeeSalary));

        if (deleteResult && deleteResult.status === 200) {

          dispatch(getEmployeeSalaryChange(employeeProfile.idEmp));
          dispatch(getEmployeeProfile(employeeProfile.idEmp));
          
          Swal.fire({
            title: t("DataSaveSuccessful"),
            icon: "success",
            timer: 2000,
            confirmButtonColor: "#46CBE2",
            customClass: {
              container: "my-swal-container",
            }
          });
        } else if (deleteResult && deleteResult.data && deleteResult.data.message) {
          Swal.fire({
            title: t(`${deleteResult.data.message}`),
            icon: "error",
            confirmButtonColor: "#46CBE2",
            customClass: {
              container: "my-swal-container",
            }
          });
        } else {
          Swal.fire({
            title: t("AnErrorOccurred"),
            icon: "error",
            confirmButtonColor: "#46CBE2",
            customClass: {
              container: "my-swal-container",
            }
          });
        }

      }
    })
  }

  return (
    <StyledRoot>
      <StyledWrapTop>
        <div>
          <ButtonBlue
            size="small"
            startIcon={<EditIcon />}
            onClick={handleOpenDrawerAdd}
          >
            {t("Edit")}
          </ButtonBlue>
        </div>
      </StyledWrapTop>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("PaymentTypeName")}
            </StyledHeadLabel>
            <div>

              <Typography variant="h6">
                {(employeeProfile.salary && employeeProfile.salary.paymentTypeName) ? (
                  <Fragment>
                    {employeeProfile.salary.paymentTypeName}
                    {(employeeProfile.roundPayment) && (
                      <Typography color="text.secondary" component="span" pl={1}>
                        {`(${t("PaymentRound")}: ${employeeProfile.roundPayment})`}
                      </Typography>
                    )}
                  </Fragment>
                ) : (
                  "-"
                )}
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div>
            <StyledHeadLabel color="text.secondary">
              {t("Salary")}
            </StyledHeadLabel>
            <div className="salary">
              <div
                className="wrap-show-hide-personalId"
                onMouseEnter={() => setShowPersonalId(true)}
                onMouseLeave={() => setShowPersonalId(false)}
              >
                {showPersonalId ? (
                  <StyledTextValue variant="h6" align="center">
                    {employeeProfile.salaryValue
                      ? utils.numberWithCommas(employeeProfile.salaryValue)
                      : "-"}
                  </StyledTextValue>
                ) : (
                  <StyledTextValue className="dot" variant="h6" align="center">
                    {[...Array(6).keys()].map((_, index) => (
                      // <i key={index} class="fa-sharp fa-solid fa-circle"></i>
                      <i key={index} className="fas fa-times"></i>
                      // <CloseRoundedIcon key={index} />
                    ))}
                  </StyledTextValue>
                )}
              </div>
              <Typography color="text.secondary">
                {employeeProfile.salary
                  ? `/${
                      employeeProfile.salary.idPaymentType === 1
                        ? t("Day")
                        : t("Month")
                    }`
                  : "-"}
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>
      <StyledDivider />
      <div>
        <StyledHeadLabel color="text.secondary">
          {t("ChangeLog")}
        </StyledHeadLabel>
        {employeeSalary && (
          <StyledWrapChange>
            {[...employeeSalary].reverse().map((item, index) => (
              <Box display="flex" key={index} marginBottom="8px">
                <Box display="flex" alignItems="flex-start" marginRight="8px">
                  <IconButton
                    aria-label="delete"
                    size="small"
                    onClick={() => {
                      handleClickDeleteEmployeeSalaryChange(item)
                    }}
                    disabled={index > 0 || employeeSalary.length === 1}
                  >
                    <HighlightOffRoundedIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    aria-label="edit"
                    size="small"
                    onClick={() => {
                      handleClickEditSalaryChange(item)
                    }}
                    disabled={index > 0}
                  >
                    <EditNoteRoundedIcon fontSize="inherit" />
                  </IconButton>
                </Box>
                <Box flexGrow="1">
                  <Box display="flex">
                    <Typography color="text.third" fontWeight="600" marginRight="8px">
                      {`[${renderDate(item.start)} - ${renderDate(item.end)}]`}
                    </Typography>
                    <Typography fontWeight="600">
                      {`${utils.numberWithCommas(item.salaryValue)} ${t("Baht")}`}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" fontWeight="500">
                    {`${t("PaymentType")}: ${item.idPaymentType === 1? t("Daily"): t("Monthly")}`}
                  </Typography>
                  <Typography paddingTop="4px" color="text.secondary" fontWeight="500">
                    {`${t("Reason")}: ${item.remark? item.remark: "-"}`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </StyledWrapChange>
        )}
      </div>

      <DrawerEditSalaryChange
        drawerConfig={drawerEditSalaryChangeConfig}
        handleClose={handleCloseEditSalaryChange}
      />
    </StyledRoot>
  );
}

export default PaymentChange;
